
import React, { useEffect, useState } from "react";
import { useCart } from "../../components/CartContext";
import { useRestaurantSession } from "../../components/RestaurantSessionProvider";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import {
  createRazorpayOrder,
  verifyRazorpayPayment,
  resetPayment,
} from "../../actions/paymentActions";

import "./css/PaymentScreen.css";

function PaymentScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { restaurantId, tableId } = useParams();
  const { sessionToken } = useRestaurantSession();
  const { clearCart } = useCart();

  const { order } = useSelector((state) => state.order || {});
  const { restaurantName } = useSelector((state) => state.menu || {});
  const { loading, error } = useSelector((state) => state.payment || {});

  const [paymentStarted, setPaymentStarted] = useState(false);
  const [localError, setLocalError] = useState("");

  const basePath = `/${restaurantId}/table/${tableId}`;

  // Redirect if there is no created order.
  useEffect(() => {
    if (!order) {
      navigate(`${basePath}/checkout`, { replace: true });
    }
  }, [order, navigate, basePath]);

  // Clear any previous payment status.
  useEffect(() => {
    dispatch(resetPayment());
  }, [dispatch]);

  // Load the Razorpay Checkout SDK.
  useEffect(() => {
    const scriptUrl = "https://checkout.razorpay.com/v1/checkout.js";

    if (document.querySelector(`script[src="${scriptUrl}"]`)) {
      return;
    }

    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;

    document.body.appendChild(script);
  }, []);

  const handlePayment = async () => {
    if (!order || paymentStarted || loading) {
      return;
    }

    setLocalError("");

    if (!sessionToken) {
      setLocalError(
        "Dining session is not ready. Please scan the table QR code again."
      );
      return;
    }

    if (!window.Razorpay) {
      setLocalError(
        "Payment service is still loading. Please try again."
      );
      return;
    }

    setPaymentStarted(true);

    try {
      // Create Razorpay order through Django.
      const razorpayData = await dispatch(
        createRazorpayOrder(
          restaurantId,
          order.id,
          sessionToken
        )
      );

      if (
        !razorpayData ||
        !razorpayData.key_id ||
        !razorpayData.razorpay_order_id ||
        !razorpayData.amount ||
        !razorpayData.currency
      ) {
        throw new Error(
          "Incomplete payment information returned by the server."
        );
      }

      const options = {
        key: razorpayData.key_id,
        amount: razorpayData.amount,
        currency: razorpayData.currency,

        name: restaurantName || "Restaurant",
        description: `Order #${order.order_number}`,
        order_id: razorpayData.razorpay_order_id,

        handler: async function (response) {
          try {
            if (
              !response?.razorpay_order_id ||
              !response?.razorpay_payment_id ||
              !response?.razorpay_signature
            ) {
              throw new Error(
                "Incomplete Razorpay payment response."
              );
            }

            // Verify payment on the Django backend.
            await dispatch(
              verifyRazorpayPayment(
                restaurantId,
                {
                  order_id: order.id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                },
                sessionToken
              )
            );

            // Only clear cart after backend verification.
            clearCart();

            navigate(`${basePath}/success`, {
              replace: true,
            });
          } catch (verificationError) {
            console.error(
              "Payment verification failed:",
              verificationError
            );

            setLocalError(
              "Payment verification could not be completed. " +
              "Please check your payment status before trying again."
            );

            setPaymentStarted(false);
          }
        },

        modal: {
          ondismiss: () => {
            setPaymentStarted(false);
          },
        },

        prefill: {
          name: "",
          email: "",
          contact: "",
        },

        notes: {
          restaurant_id: String(restaurantId),
          table_id: String(tableId),
          order_number: String(order.order_number),
        },

        theme: {
          color: "#29382f",
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", function () {
        setLocalError(
          "Payment failed. Please check your payment status before retrying."
        );
        setPaymentStarted(false);
      });

      razorpay.open();
    } catch (paymentError) {
      console.error(
        "Payment initialization failed:",
        paymentError
      );

      setLocalError(
        paymentError?.response?.data?.detail ||
        paymentError?.message ||
        "Unable to start payment."
      );

      setPaymentStarted(false);
    }
  };

  if (!order) {
    return null;
  }

  return (
    <div className="payment-page theme2-payment-page">
      <div className="payment-container">
        {/* HEADER */}
        <div className="payment-header">
          <h1>Payment</h1>
          <p>Complete your payment</p>
        </div>

        {/* ERRORS */}
        {(localError || error) && (
          <div className="payment-error" role="alert">
            {localError ||
              (typeof error === "string"
                ? error
                : "Payment failed")}
          </div>
        )}

        {/* ORDER DETAILS */}
        <div className="payment-order-card">
          <div>
            <span>Restaurant</span>
            <strong>{restaurantName || "Restaurant"}</strong>
          </div>

          <div>
            <span>Order Number</span>
            <strong>#{order.order_number}</strong>
          </div>

          <div>
            <span>Total Amount</span>
            <strong>
              ₹{Number(order.total).toFixed(2)}
            </strong>
          </div>
        </div>

        {/* PAYMENT */}
        <div className="payment-card">
          <h2>Online Payment</h2>

          <p>
            You will be redirected to Razorpay to complete
            your payment.
          </p>

          <button
            type="button"
            className="pay-now-button"
            onClick={handlePayment}
            disabled={
              loading ||
              paymentStarted ||
              !sessionToken
            }
          >
            {loading || paymentStarted
              ? "Opening Payment..."
              : !sessionToken
              ? "Dining Session Unavailable"
              : `Pay ₹${Number(order.total).toFixed(2)}`}
          </button>
        </div>

        {/* BACK */}
        <button
          type="button"
          className="payment-back-button"
          onClick={() => navigate(`${basePath}/order`)}
          disabled={paymentStarted}
        >
          ← Back to Order
        </button>
      </div>
    </div>
  );
}

export default PaymentScreen;